import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { DeleteConfirmDialog } from '../../../../components';
import { FEATURES } from '../../../../core';
import { loadDiagnosis, subscribeDiagnoses } from '../../record';
import { canDeleteDiagnosis, deleteDiagnosis } from '../../server';

/**
 * 결과 상세 헤더의 이 회차 삭제 버튼(`ResultHeaderActions` 가 공유 버튼과 나란히 둔다).
 *
 * **레코드는 저장소에서 읽는다 — 서고 사라짐은 저장이 날 때마다, id 는 누르는 시점에.** 상세 화면의
 * `useMemo` 사본은 화면을 연 때의 것이고, 회차 마감 뒤 같은 레코드가 한 번 더 저장되며 `analyId` 가
 * 채워지기도 한다 — 사본으로 판정하면 옛 값으로 서버 id 를 가리킨다.
 *
 * 서버가 먼저, 성공해야 로컬이다(`deleteDiagnosis`). 실패하면 로컬을 남기고 토스트만 띄운다 —
 * 화면에 남아 다시 누를 수 있다.
 */
export const DeleteAction = ({ recordId }: { recordId: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // 상태는 비동기라 같은 틱의 두 번째 클릭을 못 막는다 — ref 가 실제 가드다.
  const running = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const deletable = useSyncExternalStore(
    subscribeDiagnoses,
    () =>
      FEATURES.diagnosisDelete && canDeleteDiagnosis(loadDiagnosis(recordId)),
  );
  if (!deletable) {
    return null;
  }

  const confirm = async (): Promise<void> => {
    if (running.current) {
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      const record = loadDiagnosis(recordId);
      const outcome = record ? await deleteDiagnosis(record) : 'deleted';
      if (outcome === 'deleted') {
        toast.success(t('web.result.delete.done'));
        // 도는 동안 화면을 떠났으면 그 사용자를 이력으로 끌고 가지 않는다.
        if (alive.current) {
          navigate('/history', { replace: true });
        }
        return;
      }
      toast.error(t('web.result.delete.failed'));
      if (alive.current) {
        setOpen(false);
      }
    } finally {
      running.current = false;
      if (alive.current) {
        setBusy(false);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('web.result.delete.label')}
        className="flex h-11 w-11 items-center justify-center text-bk-900"
      >
        <Trash2 className="h-6 w-6" aria-hidden="true" />
      </button>
      {open && (
        <DeleteConfirmDialog
          title={t('web.result.delete.title')}
          body={t('web.result.delete.body')}
          confirmLabel={t('web.result.delete.confirm')}
          cancelLabel={t('web.result.delete.cancel')}
          busy={busy}
          onConfirm={() => void confirm()}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
};
